/**
 * Literature search contract (F13, milestone M3): strategies, sources,
 * uploads, executions, records and PRISMA identification counts.
 * Re-exported from index.ts.
 *
 * Owner: search builder (F13). Shapes follow
 * specs/features/F13-literature-search/design.md, Interfaces.
 */
import type { RecordDedupDetail } from './dedup';
import { pagePath } from './paths';

export const SEARCH_ROUTES = {
  /** GET -> 200 SearchStrategyListResponse (E83; SRCH-030). */
  searchStrategies: (slug: string) => `${pagePath(slug)}/search-strategies`,
  /** POST SearchStrategyDraftRequest -> 200 SearchStrategyDraftResponse; 502 when every source failed (E22; SRCH-020). */
  searchStrategyDraft: (slug: string) => `${pagePath(slug)}/search-strategies/draft`,
  /** PUT SaveSearchStrategyRequest -> 200 SearchStrategy, a new version (E23; SRCH-031). */
  searchStrategy: (slug: string, source: ApiSearchSource) => `${pagePath(slug)}/search-strategies/${encodeURIComponent(source)}`,
  /** POST ApproveSearchStrategyRequest -> 200 SearchStrategy (E24; SRCH-033). */
  searchStrategyApprove: (slug: string, source: ApiSearchSource) =>
    `${pagePath(slug)}/search-strategies/${encodeURIComponent(source)}/approve`,
  /** POST StrategyPreviewRequest -> 200 StrategyPreviewResponse (E85; SRCH-037). */
  searchStrategyPreview: (slug: string, source: ApiSearchSource) =>
    `${pagePath(slug)}/search-strategies/${encodeURIComponent(source)}/preview`,
  /** PUT SearchSourcesRequest -> 200 SearchStrategyListResponse (E84; SRCH-012). */
  searchSources: (slug: string) => `${pagePath(slug)}/search-sources`,
  /** PUT CitationSettings -> 200 CitationSettings (E86; SRCH-125). */
  citationSettings: (slug: string) => `${pagePath(slug)}/citation-settings`,
  /** PUT -> 204 nominates, DELETE -> 204 removes a citation seed (E87, E88; SRCH-122). */
  citationSeed: (slug: string, recordId: string) => `${pagePath(slug)}/citation-seeds/${encodeURIComponent(recordId)}`,
  /** POST multipart SearchUploadFields -> 202 SearchUploadResponse (E89); GET -> 200 SearchUploadListResponse (E90; SRCH-110 to SRCH-118). */
  searchUploads: (slug: string) => `${pagePath(slug)}/uploads`,
  /** GET -> 200 SearchExecutionListResponse (E91; SRCH-090, SRCH-162). */
  searchExecutions: (slug: string, runId: string) => `${pagePath(slug)}/search-executions?runId=${encodeURIComponent(runId)}`,
  /** GET ?limit&cursor&source&runId&kind&flag&q -> 200 RecordListResponse (E29; SRCH-141). */
  records: (slug: string) => `${pagePath(slug)}/records`,
  /** GET -> 200 RecordDetail (E92; SRCH-135). */
  searchRecord: (slug: string, recordId: string) => `${pagePath(slug)}/records/${encodeURIComponent(recordId)}`,
} as const;

/** Search sources in SRCH-010 order. Crossref and Unpaywall are lookup services, not sources. */
export const SEARCH_SOURCES = ['pubmed', 'europepmc', 'ctgov', 'openalex', 'upload'] as const;
export type SearchSource = (typeof SEARCH_SOURCES)[number];
export type ApiSearchSource = Exclude<SearchSource, 'upload'>;
export type PrismaColumn = 'database' | 'register' | 'other';
export type StrategyStatus = 'draft' | 'approved' | 'superseded';
export type ExecutionStatus = 'running' | 'ok' | 'partial' | 'failed';
export type ExecutionKind = 'search' | 'upload' | 'citation_backward' | 'citation_forward';

/** RunWarning codes the search stage adds (F13 design, Stage runner). */
export type SearchRunWarningCode =
  | 'source_failed'
  | 'hits_not_retrieved'
  | 'unresolved_seed'
  | 'window_not_supported'
  | 'strategy_changed_full_search';

/** details.reason of a 409 conflict from the search endpoints and from starting a run (SRCH-013, SRCH-014). */
export type SearchConflictReason = 'protocol_not_frozen' | 'strategy_not_approved' | 'strategy_invalid' | 'duplicate_upload';

export interface StrategyIssue {
  /** For example 'unbalanced_quotes', 'unknown_field_tag', 'unknown_mesh_term'. */
  code: string;
  /** British English, for display. */
  message: string;
  /** 0-based UTF-16 offset in queryText, when it applies. */
  at: number | null;
  severity: 'error' | 'warning';
}

export interface ConceptBlock {
  name: 'population' | 'intervention' | 'comparator' | 'outcome' | 'design' | 'other';
  /** For example "Adults with acute myocardial infarction". */
  label: string;
  /** In the source's syntax, for example "Myocardial Infarction[mh]". */
  terms: string[];
  /** The model's reason, shown to the reviewer; at most 600 characters. */
  rationale: string;
}

export interface SearchStrategy {
  /** 'ss_<uuid>'. */
  id: string;
  source: ApiSearchSource;
  /** 1, 2, 3 ... per page and source. */
  version: number;
  status: StrategyStatus;
  /** Verbatim (SRCH-032). */
  queryText: string;
  /** Allowed keys per source (F13 design, Data: Strategy filters). */
  filters: Record<string, string>;
  /** Model drafts only. */
  blocks: ConceptBlock[] | null;
  /** As validated at save time. */
  issues: StrategyIssue[];
  draftedBy: { provider: 'anthropic'; model: string; modelCallId: string } | null;
  /** User id for saves; null for model drafts. */
  createdBy: string | null;
  createdAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
  /** SRCH-036. */
  amendmentNote: string | null;
}

export interface SourceStrategies {
  latest: SearchStrategy | null;
  approved: SearchStrategy | null;
  /** Newest first. */
  history: SearchStrategy[];
  amendments: { version: number; note: string; approvedAt: string; approvedBy: string }[];
  lastPreview: StrategyPreviewResponse | null;
}

/** GET SEARCH_ROUTES.searchStrategies (SRCH-030). */
export interface SearchStrategyListResponse {
  /** The caller's workspace role on this page, so the screen offers only the actions the server allows (SRCH-160). */
  role: 'viewer' | 'editor' | 'owner';
  enabledSources: ApiSearchSource[];
  strategies: Partial<Record<ApiSearchSource, SourceStrategies>>;
  citationChasing: CitationSettings;
  incremental: Partial<Record<ApiSearchSource, boolean>>;
}

/** POST SEARCH_ROUTES.searchStrategyDraft (SRCH-020). */
export interface SearchStrategyDraftRequest {
  sources: ApiSearchSource[];
}

export interface SearchStrategyDraftResponse {
  drafts: SearchStrategy[];
  /** ai_unavailable: the model could not be reached for that source after its retries (SRCH-028). */
  failures: { source: ApiSearchSource; code: 'ai_invalid_output' | 'ai_refused' | 'ai_unavailable'; errors: string[] }[];
}

/** PUT SEARCH_ROUTES.searchStrategy (SRCH-031). */
export interface SaveSearchStrategyRequest {
  queryText: string;
  filters: Record<string, string>;
}

/** POST SEARCH_ROUTES.searchStrategyApprove (SRCH-033, SRCH-036). */
export interface ApproveSearchStrategyRequest {
  version: number;
  amendmentNote?: string;
}

/** POST search-strategies/{source}/preview (E85; SRCH-037). */
export interface StrategyPreviewRequest {
  version: number;
}

export interface StrategyPreviewResponse {
  version: number;
  hitCount: number;
  countedAt: string;
  /** PubMed only. */
  queryTranslation: string | null;
  warnings: StrategyIssue[];
}

/** PUT search-sources (E84; SRCH-012). */
export interface SearchSourcesRequest {
  enabledSources: ApiSearchSource[];
}

/** PUT citation-settings (E86; SRCH-125). */
export interface CitationSettings {
  enabled: boolean;
  directions: ('backward' | 'forward')[];
}

/** POST uploads, multipart/form-data (E89; SRCH-110, SRCH-111). */
export interface SearchUploadFields {
  /** .ris, .nbib, .txt or .csv. */
  file: Blob;
  /** 1 to 200 characters. */
  databaseName: string;
  /** 0 to 200 characters, for example "Ovid". */
  platform: string;
  /** YYYY-MM-DD, not after today. */
  searchedOn: string;
  /** 1 to 20 000 characters, verbatim. */
  strategyText: string;
  prismaColumn: PrismaColumn;
}

export interface SearchUploadResponse {
  /** 'up_<uuid>'. */
  uploadId: string;
  format: 'ris' | 'medline' | 'csv';
  /** Valid entries. */
  recordCount: number;
  entryCount: number;
  /** The first 200. */
  problems: { entry: number; line: number; reason: string }[];
  problemCount: number;
  status: 'pending' | 'ingested' | 'failed';
  runId: string | null;
  /** The search details recorded with the upload (SRCH-111), verbatim. */
  databaseName: string;
  platform: string;
  searchedOn: string;
  strategyText: string;
  prismaColumn: PrismaColumn;
  fileName: string;
  createdAt: string;
}

/** GET SEARCH_ROUTES.searchUploads (E90), newest first. */
export interface SearchUploadListResponse {
  uploads: SearchUploadResponse[];
}

export interface SearchExecution {
  /** 'se_<uuid>'. */
  id: string;
  runId: string;
  /** Citation executions are 'openalex' even when Crossref references were the fallback (SRCH-120). */
  source: SearchSource;
  kind: ExecutionKind;
  prismaColumn: PrismaColumn;
  /** "PubMed", or the upload's database name. */
  name: string;
  strategyId: string | null;
  strategyVersion: number | null;
  uploadId: string | null;
  queryText: string | null;
  filters: Record<string, string>;
  executedAt: string;
  finishedAt: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  hitCount: number | null;
  retrievedCount: number;
  newRecordCount: number;
  status: ExecutionStatus;
  errorCode: string | null;
  warnings: StrategyIssue[];
}

/** GET search-executions?runId= (E91; SRCH-162). */
export interface SearchExecutionListResponse {
  executions: SearchExecution[];
}

export type RecordKind =
  | 'journal_article'
  | 'preprint'
  | 'conference_abstract'
  | 'trial_registration'
  | 'notice'
  | 'review'
  | 'other';

export interface RecordAuthor {
  family: string;
  given: string | null;
}

export interface RecordListing {
  id: string;
  kind: RecordKind;
  title: string;
  /** The first 10 only in listings. */
  authors: RecordAuthor[];
  journal: string | null;
  year: number | null;
  doi: string | null;
  pmid: string | null;
  pmcid: string | null;
  registryId: string | null;
  sources: { source: SearchSource; sourceRecordId: string; executionId: string }[];
  firstRunId: string | null;
  flags: {
    retracted: boolean;
    expressionOfConcern: boolean;
    /** 'pubmed', 'openalex', 'crossref:<source>'. */
    source: string | null;
    /** Notice PMID or DOI. */
    notice: string | null;
    registryUpdated: boolean;
  };
  /** F14. */
  duplicateOf: string | null;
  /** F14. */
  studyClusterId: string | null;
  /** F15. */
  screening: { stage: 'title_abstract' | 'full_text'; state: string; verdict: 'include' | 'exclude' | null }[];
}

/**
 * GET SEARCH_ROUTES.searchRecord (E92; SRCH-135): every author and the abstract, with the
 * record's duplicates, identifiers, study clusters and open candidates (F14 DEDUP-091).
 */
export interface RecordDetail extends RecordListing, RecordDedupDetail {
  abstract: string | null;
}

/** GET SEARCH_ROUTES.records (SRCH-141; api.md E29). */
export interface RecordListResponse {
  records: RecordListing[];
  nextCursor: string | null;
}

/** PRISMA identification for one source or upload (SRCH-150 to SRCH-153). */
export interface PrismaSourceCount {
  source: SearchSource;
  name: string;
  uploadId: string | null;
  /** Null when the source failed: never shown as zero (SRCH-152). */
  count: number | null;
  /** Uploads: entries in the file. */
  entries?: number;
  status: 'ok' | 'partial' | 'failed';
}

/** F13's part of the PRISMA flow; composed with F14's and F15's parts (RunPrisma, and F20's PrismaCounts). */
export interface PrismaIdentification {
  databases: PrismaSourceCount[];
  registers: PrismaSourceCount[];
  otherMethods: { citationSearching: number | null; other: PrismaSourceCount[] };
}

/** One entry of run.search_log_json (LIVE-035, SRCH-091). */
export interface SearchLogEntry {
  source: SearchSource;
  /** Query text as sent, without secrets; for an upload, the strategy text recorded with it. */
  query: string;
  dateFrom: string | null;
  dateTo: string;
  searchedAt: string;
  hits: number | null;
  retrieved: number;
  status: 'ok' | 'partial' | 'failed';
  error: string | null;
  /** F13 additions (F13 design, SearchLogEntryF13). Optional so the F18 shape stays valid on its own. */
  kind?: ExecutionKind;
  name?: string;
  strategyId?: string | null;
  strategyVersion?: number | null;
  uploadId?: string | null;
  newRecords?: number;
  errorCode?: string | null;
  warnings?: StrategyIssue[];
}
